import React from 'react'

import { v4 } from 'uuid'

import { Table, Space, Divider, Popconfirm, Button, Row, Col, Alert } from 'antd'

import { LikeTwoTone, DislikeTwoTone, CloseCircleTwoTone } from '@ant-design/icons'

import { useCacheService, getServicesById, getCategoriesById } from '../../../hooks/cache/service'
import { useCacheLanguage, getLanguagesById } from '../../../hooks/cache/language'
import { useCacheLanguagePair, getLanguagePairsById } from '../../../hooks/cache/languagePair'
import { useCacheUnit, getUnitsById } from '../../../hooks/cache/unit'

import {
  CONST_LANGUAGE_TYPE_NONE,
  CONST_LANGUAGE_TYPE_SINGLE,
  CONST_LANGUAGE_TYPE_PAIR
} from '../../../config/consts/language'

const pricesColumns = (
  edit,
  remove,
  servicesById,
  categoriesById,
  languagesById,
  languagesPairById,
  unitsById,
  review
) => {
  const columns = [
    {
      title: 'Service',
      dataIndex: 'service',
      render: (service) => {
        if (service) {
          return <>{servicesById[service]?.name}</>
        }
        return <>All</>
      }
    },
    {
      title: 'Category',
      dataIndex: 'category',
      render: (category) => {
        if (category) {
          return <>{categoriesById && categoriesById[category]?.name}</>
        }
        return <>All</>
      }
    },
    {
      title: 'Unit',
      dataIndex: 'unit',
      render: (unit) => {
        return <>{unitsById[unit]?.name}</>
      }
    },

    {
      title: 'Language',
      dataIndex: '',
      render: (text, record) => {
        // language is bound to service
        if (!record.service) {
          return <>All</>
        }
        if (servicesById[record.service].language === CONST_LANGUAGE_TYPE_NONE) {
          return <>-</>
        }

        if (servicesById[record.service].language === CONST_LANGUAGE_TYPE_SINGLE && record.language_family) {
          return <>{record.language_family}</>
        }

        if (servicesById[record.service].language === CONST_LANGUAGE_TYPE_SINGLE && record.language) {
          return <>{languagesById[record.language].name}</>
        }
        if (servicesById[record.service].language === CONST_LANGUAGE_TYPE_PAIR && record.language_pair) {
          return <>{languagesPairById[record.language_pair].name}</>
        }

        return <>All</>
      }
    },
    {
      title: 'Price',
      dataIndex: 'amount',
      render: (amount, record) => {
        if (amount) {
          return <>{amount}</>
        }
        return <>{record.amount}</>
      }
    }
  ]

  if (review) {
    columns.push({
      title: 'Requested',
      dataIndex: 'requested',
      render: (requested) => {
        if (requested) {
          return <>{requested}</>
        }
      }
    })
  }

  if (edit || remove) {
    columns.push({
      title: 'Action',
      dataIndex: '',
      render: (text, record) => {
        return (
          <Space size='small' split={<Divider type='vertical' />}>
            <Button size='small' type='link' onClick={() => edit(record.id)}>
              Edit
            </Button>
            <Popconfirm
              title='Are you sure?'
              onConfirm={() => remove(record.id)}
              okText='Yes'
              cancelText='No'
            >
              <Button size='small' type='link'>
                Delete
              </Button>
            </Popconfirm>
          </Space>
        )
      }
    })
  }

  return columns
}

const mergePrices = (prices, pricesReview) => {
  const exists = []
  const merged = []

  for (let i = 0; i < pricesReview.length; ++i) {
    const pr = pricesReview[i]
    let priceIndex = -1
    let found = false

    for (let j = 0; j < prices.length; ++j) {
      const p = prices[j]
      if (
        p?.unit == pr?.unit &&
        p?.service == pr?.service &&
        p?.category == pr?.category &&
        p?.language == pr?.language &&
        p?.language_pair == pr?.language_pair &&
        p?.language_family == pr?.language_family
      ) {
        priceIndex = j
        found = true
      }
    }

    if (!found) {
      merged.push({ ...pr, amount: 'New', requested: pr.amount, id: v4() })
    } else {
      exists.push(priceIndex)
      const p = prices[priceIndex]
      merged.push({ ...p, requested: pr.amount, id: v4() })
    }
  }

  for (let i = 0; i < prices.length; ++i) {
    if (!exists.includes(i)) {
      const p = prices[i]
      merged.push({ ...p, requested: 'Deleted', id: v4() })
    }
  }

  return merged
}

const Prices = (props) => {
  const { data: servicesRaw, isLoading: isServiceLoading } = useCacheService()
  const { data: languagesRaw, isLoading: isLanguageLoading } = useCacheLanguage()
  const { data: languagePairsRaw, isLoading: isLanguagePairLoading } = useCacheLanguagePair()
  const { data: unitsRaw, isLoading: isUnitsLoading } = useCacheUnit()
  const servicesById = getServicesById(servicesRaw)
  const categoriesById = getCategoriesById(servicesRaw)
  const languagesById = getLanguagesById(languagesRaw)
  const languagePairsById = getLanguagePairsById(languagePairsRaw)
  const unitsById = getUnitsById(unitsRaw)

  const {
    prices,
    pricesReview,
    validatePrices,
    rejectPrices,
    cancelPrices
  } = props

  let merged
  if (pricesReview) {
    merged = mergePrices(prices, pricesReview)
  } else {
    merged = prices
  }

  if (isLanguageLoading || isLanguagePairLoading || isServiceLoading || isUnitsLoading) {
    return null
  }

  return (
    <Table
      bordered
      columns={pricesColumns(
        props.editPrice,
        props.removePrice,
        servicesById,
        categoriesById,
        languagesById,
        languagePairsById,
        unitsById,

        pricesReview
      )}
      rowKey={(r) => r.id}
      dataSource={merged}
      size='small'
      pagination={false}
      title={() => {
        /*pricesReview && validateReview && rejectReview && ( */
        if (!pricesReview) {
          return null
        }
        return (
          <>
            <Row>
              {pricesReview && (
                <Col flex='auto'>
                  {(pricesReview || []).length !== 0 && (
                    <Alert
                      type='warning'
                      message='Price changes are waiting for review.'
                      banner
                      showIcon
                    />
                  )}
                </Col>
              )}
              {pricesReview && validatePrices && rejectPrices && (
                <Col flex='240px' style={{ paddingTop: '4px' }} align='right'>
                  <Button
                    onClick={() => validatePrices()}
                    icon={
                      <>
                        <LikeTwoTone twoToneColor='#52c41a' /> <Divider type='vertical' />
                      </>
                    }
                  >
                    Validate{' '}
                  </Button>
                  <Divider type='vertical' />
                  <Button
                    onClick={() => rejectPrices()}
                    icon={
                      <>
                        <DislikeTwoTone twoToneColor='#eb2f96' />
                        <Divider type='vertical' />
                      </>
                    }
                  >
                    Reject
                  </Button>
                </Col>
              )}
              {pricesReview && cancelPrices && (
                <Col flex='160px' style={{ paddingTop: '4px' }} align='right'>
                  <Button
                    onClick={() => cancelPrices()}
                    icon={
                      <>
                        <CloseCircleTwoTone />
                        <Divider type='vertical' />
                      </>
                    }
                  >
                    Cancel Review
                  </Button>
                </Col>
              )}
            </Row>
          </>
        )
      }}
    />
  )
}

export default Prices
